import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import {
  pursuitActionsQueryKeys,
  pursuitQueryKeys,
} from '@/hooks/pursuits/types'
import type {
  SalesforceAdditionalFields,
  SalesforceOpportunity,
} from '@/hooks/salesforce/types'

import type { UUID } from '@/types/Id'

interface UseCreateSalesforceOpportunityArgs {
  orgId: UUID | ''
}

interface CreateSalesforceOpportunityArgs {
  pursuit_id: UUID
  additional_fields: SalesforceAdditionalFields
}

const createSalesforceOpportunity = async (
  req: CreateSalesforceOpportunityArgs,
  orgId: UUID | '',
  accessToken: string | undefined | null,
): Promise<SalesforceOpportunity> => {
  if (!accessToken) {
    throw new SweetspotError({
      name: 'NO_ACCESS_TOKEN_PROVIDED_ERROR',
      message: 'No access token provided',
    })
  }
  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/salesforce/opportunities`,
    {
      method: 'POST',
      body: JSON.stringify(req),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }
  return (await res.json()) as SalesforceOpportunity
}

export const useCreateSalesforceOpportunity = ({
  orgId,
}: UseCreateSalesforceOpportunityArgs) => {
  const { accessToken } = useAuthInfo()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation<
    SalesforceOpportunity,
    Error,
    CreateSalesforceOpportunityArgs,
    void
  >({
    mutationFn: (req) => createSalesforceOpportunity(req, orgId, accessToken),
    onError: (err, req, context) => {
      handleError(err)
    },
    onSuccess: (data, req) => {
      const key = pursuitQueryKeys.pursuit({ pursuitId: req.pursuit_id })

      const actionsKey = pursuitActionsQueryKeys.pursuit({
        pursuitId: req.pursuit_id,
      })
      setTimeout(() => {
        // give the server time to create the action
        queryClient.invalidateQueries({ queryKey: actionsKey })
      }, 3000)

      return queryClient.invalidateQueries({ queryKey: key })
    },
  })
}
